import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2.5 whitespace-nowrap rounded-md text-sm font-medium transition-all [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-6 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "rounded-full bg-brand-blue border-2 border-brand-violet-400 text-white font-medium button-primary-shadow hover:shadow-none transition-all disabled:pointer-events-none disabled:bg-brand-cinder-900 disabled:border-brand-cinder-800 disabled:text-white/50 disabled:shadow-none",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 disabled:pointer-events-none disabled:opacity-50",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50 disabled:pointer-events-none disabled:opacity-50",
        // Outline on blue background (transparent with violet border)
        "outline-light":
          "rounded-full bg-transparent border-2 border-brand-violet-50 text-white font-medium button-outline-light-shadow hover:shadow-none hover:bg-white/10 hover:border-white transition-all disabled:pointer-events-none disabled:opacity-50",
        // White/light button with dark text
        light:
          "rounded-full bg-white border-2 border-brand-violet-50 text-brand-cinder-950 font-medium button-light-shadow hover:shadow-none hover:bg-brand-violet-50 transition-all disabled:pointer-events-none disabled:opacity-50",
      },
      size: {
        default: "h-12 px-6 py-3",
        icon: "w-12 h-12 p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
